"use client"

import { useEffect, useMemo } from "react"
import { motion } from "framer-motion"
import "bootstrap-icons/font/bootstrap-icons.css"
import { articles } from "./blogData"
import "./BlogArticle.css"

const setMeta = (selector, attribute, value) => {
  let element = document.head.querySelector(selector)
  if (!element) {
    element = document.createElement("meta")
    const [key, name] = attribute
    element.setAttribute(key, name)
    document.head.appendChild(element)
  }
  element.setAttribute("content", value)
}

const headingStopWords = new Set([
  "a", "an", "and", "at", "for", "from", "in", "is", "of", "on",
  "or", "the", "to", "with", "your",
])

const isHeading = (line) => {
  if (!line || line.length > 90 || /[.!;,]$/.test(line)) return false
  if (line === "Conclusion" || /^Step \d+:/i.test(line) || line.endsWith("?")) return true

  const significantWords = line
    .replace(/[^A-Za-z0-9\s-]/g, "")
    .split(/\s+/)
    .filter(Boolean)
    .filter((word) => !headingStopWords.has(word.toLowerCase()))

  return significantWords.length > 0 && significantWords.every(
    (word) => /^\d+$/.test(word) || /^[A-Z]/.test(word),
  )
}

const headingId = (heading) => heading
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, "-")
  .replace(/^-|-$/g, "")

const renderInlineContent = (text) => text
  .split(/(\[\[[^|]+\|https?:\/\/[^\]]+\]\])/g)
  .filter(Boolean)
  .map((part, index) => {
    const link = part.match(/^\[\[([^|]+)\|(https?:\/\/[^\]]+)\]\]$/)
    if (!link) return part

    return (
      <a
        className="article-inline-link"
        href={link[2]}
        target="_blank"
        rel="noopener noreferrer"
        key={`${link[2]}-${index}`}
      >
        {link[1]}
      </a>
    )
  })

const buildArticleBlocks = (content) => {
  const blocks = []

  for (let index = 0; index < content.length; index += 1) {
    const line = content[index]

    if (line.endsWith(":")) {
      const items = []
      let nextIndex = index + 1

      while (nextIndex < content.length) {
        const candidate = content[nextIndex]
        if (candidate.length > 90 || /[.!]$/.test(candidate)) break
        items.push(candidate)
        nextIndex += 1
      }

      blocks.push({ type: "paragraph", text: line })
      if (items.length >= 2) {
        blocks.push({ type: "list", items })
        index = nextIndex - 1
      }
      continue
    }

    blocks.push({ type: isHeading(line) ? "heading" : "paragraph", text: line })
  }

  return blocks
}

const BlogArticle = ({ slug }) => {
  const article = articles.find((item) => item.slug === slug)
  const blocks = useMemo(() => buildArticleBlocks(article?.content || []), [article])
  const tableOfContents = blocks.filter((block) => block.type === "heading").slice(0, 8)

  useEffect(() => {
    if (!article) return undefined

    const previousTitle = document.title
    const imageUrl = new URL(article.image, window.location.origin).toString()
    document.title = `${article.title} | Katchit Blog`
    setMeta('meta[name="description"]', ["name", "description"], article.excerpt)
    setMeta('meta[property="og:title"]', ["property", "og:title"], article.title)
    setMeta('meta[property="og:description"]', ["property", "og:description"], article.excerpt)
    setMeta('meta[property="og:image"]', ["property", "og:image"], imageUrl)
    setMeta('meta[name="twitter:title"]', ["name", "twitter:title"], article.title)
    setMeta('meta[name="twitter:description"]', ["name", "twitter:description"], article.excerpt)
    setMeta('meta[name="twitter:image"]', ["name", "twitter:image"], imageUrl)

    return () => { document.title = previousTitle }
  }, [article])

  if (!article) {
    return (
      <main className="article-not-found">
        <div><span>404</span><h1>We couldn't find that story.</h1><a href="/#blog">Back to all blogs</a></div>
      </main>
    )
  }

  const relatedArticles = articles.filter((item) => item.id !== article.id).slice(0, 3)

  return (
    <div className="article-page">
      <header className="article-header">
        <div className="container article-header-inner">
          <a href="/" className="article-logo" aria-label="Katchit home"><img src="/assets/Frame.svg" alt="Katchit" /></a>
          <a href="/#blog" className="article-back-link"><i className="bi bi-arrow-left" aria-hidden="true" />Back to blogs</a>
        </div>
      </header>

      <main>
        <section className="article-hero">
          <div className="article-hero-glow" />
          <div className="container article-hero-grid">
            <motion.div className="article-hero-copy" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <div className="article-breadcrumb">
                <a href="/">Home</a><i className="bi bi-chevron-right" aria-hidden="true" />
                <a href="/#blog">Blog</a><i className="bi bi-chevron-right" aria-hidden="true" /><span>{article.category}</span>
              </div>
              <span className="article-category">{article.category}</span>
              <h1>{article.title}</h1>
              <p>{article.excerpt}</p>
              <div className="article-meta">
                <span><i className="bi bi-clock" aria-hidden="true" />{article.readTime}</span>
                <span><i className="bi bi-journal-check" aria-hidden="true" />Katchit Editorial</span>
              </div>
            </motion.div>

            <motion.div className="article-hero-image" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.55, delay: 0.1 }}>
              <img src={article.image} alt={article.title} />
              <span className="article-image-badge"><i className="bi bi-stars" aria-hidden="true" />The Katchit Edit</span>
            </motion.div>
          </div>
        </section>

        <section className="article-body-section">
          <div className="container article-layout">
            <article className="article-content">
              {blocks.map((block, index) => {
                if (block.type === "heading") {
                  return <h2 id={headingId(block.text)} key={`${block.text}-${index}`}>{block.text}</h2>
                }
                if (block.type === "list") {
                  return (
                    <ul className="article-list" key={`list-${index}`}>
                      {block.items.map((item) => (
                        <li key={item}><i className="bi bi-check2-circle" aria-hidden="true" /><span>{item}</span></li>
                      ))}
                    </ul>
                  )
                }
                return <p className={index === 0 ? "article-lead" : undefined} key={`${block.text}-${index}`}>{renderInlineContent(block.text)}</p>
              })}

              <div className="article-cta">
                <div><span>Ready when you are</span><h3>Find your next experience on Katchit</h3></div>
                <a href="https://play.google.com/store/apps/details?id=com.katchit.customer_app" target="_blank" rel="noopener noreferrer">
                  Explore the app<i className="bi bi-arrow-up-right" aria-hidden="true" />
                </a>
              </div>
            </article>

            <aside className="article-sidebar">
              <nav className="article-sidebar-card" aria-label="Article sections">
                <span className="article-sidebar-label">In this story</span>
                <h3>Explore this guide</h3>
                <ul>
                  {tableOfContents.map((block) => (
                    <li key={block.text}><a href={`#${headingId(block.text)}`}><i className="bi bi-arrow-right-short" aria-hidden="true" />{block.text}</a></li>
                  ))}
                </ul>
              </nav>
            </aside>
          </div>
        </section>

        <section className="related-section">
          <div className="container">
            <div className="related-heading"><div><span>Keep reading</span><h2>More from the Katchit Edit</h2></div><a href="/#blog">View all stories</a></div>
            <div className="related-grid">
              {relatedArticles.map((item) => (
                <a href={`/blog/${item.slug}`} className="related-card" key={item.id}>
                  <img src={item.image} alt="" /><div><span>{item.category}</span><h3>{item.title}</h3><p>{item.readTime}</p></div>
                </a>
              ))}
            </div>
          </div>
        </section>
      </main>

      <footer className="article-footer">
        <div className="container"><img src="/assets/Frame.svg" alt="Katchit" /><p>Fresh stories, local discoveries and easier everyday plans.</p><a href="/#blog">Explore all blogs</a></div>
      </footer>
    </div>
  )
}

export default BlogArticle
